import React from "react";
import {
  calculateMaterialLineCost,
  calculateProcessLineCost,
  calculateSheetTotals,
  formatMoney,
} from "@/lib/api/manufacturing";
import { SortableRowHandle, useSortableRows } from "@/hooks/useSortableRows";

function sortByOrder(left, right) {
  return (Number(left.sort_order || 0) - Number(right.sort_order || 0)) || String(left.id || "").localeCompare(String(right.id || ""));
}

function formatQuantity(value) {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return "0";
  return Number.isInteger(number) ? String(number) : number.toFixed(4).replace(/0+$/, "").replace(/\.$/, "");
}

export function costSheetProcessTotal(processLine = {}) {
  const ownTotal = Number(processLine.line_cost ?? calculateProcessLineCost(processLine));
  const materialTotal = (processLine.material_lines || []).reduce(
    (sum, line) => sum + Number(line.line_cost ?? calculateMaterialLineCost(line)),
    0
  );
  return {
    ownTotal: Number(ownTotal.toFixed(4)),
    materialTotal: Number(materialTotal.toFixed(4)),
    total: Number((ownTotal + materialTotal).toFixed(4)),
  };
}

function buildTotals(sheet, processLines) {
  if (sheet?.totals) return sheet.totals;
  const materialLines = sheet?.material_lines || processLines.flatMap((line) => line.material_lines || []);
  return calculateSheetTotals({ ...(sheet || {}), process_lines: processLines, material_lines: materialLines });
}

function MultipliedMoney({ value, multiplier }) {
  const amount = Number(value || 0);
  return (
    <div className="text-right">
      <div>{formatMoney(amount)}</div>
      {multiplier !== 1 && <div className="text-xs text-gray-500">Total {formatMoney(amount * multiplier)}</div>}
    </div>
  );
}

function MultipliedQuantity({ line, multiplier }) {
  const quantity = Number(line.quantity || 0);
  const unit = line.unit || line.material?.default_unit || "";
  const total = quantity * multiplier;
  return (
    <div className="text-right">
      <div>{formatQuantity(quantity)} {unit}</div>
      {multiplier !== 1 && <div className="text-xs text-gray-500">Total {formatQuantity(total)} {unit}</div>}
    </div>
  );
}

export function CostSheetProcessMaterialsView({
  sheet = null,
  processLines: processLinesProp = null,
  activeProcessId = "",
  onActiveProcessChange,
  onReorderProcesses,
  onReorderMaterials,
  sheetTotals: sheetTotalsProp = null,
  quantityMultiplier = 1,
  renderProcessActions,
  renderMaterialActions,
  processFooter = null,
  materialHeaderAction = null,
  emptyProcessMessage = "Añade el primer proceso para empezar.",
  emptyMaterialMessage = "Este proceso no tiene materiales.",
}) {
  const processLines = React.useMemo(
    () => [...(processLinesProp || sheet?.process_lines || [])].sort(sortByOrder),
    [processLinesProp, sheet]
  );
  const reorderEnabled = Boolean(onReorderProcesses) && processLines.length > 1;
  const { getHandleProps, getRowProps, consumeClickIfDragged, isDragging, isOver } = useSortableRows({
    items: processLines,
    onReorder: onReorderProcesses,
    disabled: !reorderEnabled,
  });
  const activeProcess = processLines.find((line) => line.id === activeProcessId) || processLines[0] || null;
  const materialLines = React.useMemo(
    () => [...(activeProcess?.material_lines || [])].sort(sortByOrder),
    [activeProcess]
  );
  const materialReorderEnabled = Boolean(onReorderMaterials) && materialLines.length > 1;
  const {
    getHandleProps: getMaterialHandleProps,
    getRowProps: getMaterialRowProps,
    isDragging: isMaterialDragging,
    isOver: isMaterialOver,
  } = useSortableRows({
    items: materialLines,
    onReorder: onReorderMaterials,
    disabled: !materialReorderEnabled,
  });
  const sheetTotals = sheetTotalsProp || buildTotals(sheet, processLines);
  const activeProcessTotals = activeProcess ? costSheetProcessTotal(activeProcess) : { ownTotal: 0, materialTotal: 0, total: 0 };
  const multiplier = Math.max(1, Number(quantityMultiplier || 1));
  const showProcessActions = Boolean(renderProcessActions);
  const showMaterialActions = Boolean(renderMaterialActions);
  const processColSpan = 2 + (reorderEnabled ? 1 : 0) + (showProcessActions ? 1 : 0);
  const materialColSpan = 4 + (materialReorderEnabled ? 1 : 0) + (showMaterialActions ? 1 : 0);

  return (
    <div className="grid gap-3 lg:grid-cols-[minmax(0,420px)_1fr] lg:items-start">
      <div className="rounded border">
        <div className="flex min-h-[3.5rem] flex-col justify-center gap-1 border-b bg-gray-50 px-3 py-2 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="font-semibold">Procesos</div>
            <div className="text-xs text-gray-500">
              {multiplier === 1 ? (
                <>Total procesos {formatMoney(sheetTotals.processOwnTotal)}</>
              ) : (
                <>Coste/prenda {formatMoney(sheetTotals.processOwnTotal)} · Total {formatMoney(Number(sheetTotals.processOwnTotal || 0) * multiplier)}</>
              )}
              {reorderEnabled ? " · Arrastra para cambiar el orden" : ""}
            </div>
          </div>
        </div>
        <table className="w-full border-collapse text-sm">
          <thead className="bg-gray-100 text-left">
            <tr>
              {reorderEnabled && <th className="w-8 border px-1 py-1" aria-label="Reordenar" />}
              <th className="border px-2 py-1">Proceso</th>
              <th className="w-28 border px-2 py-1 text-right">Precio</th>
              {showProcessActions && <th className="border px-2 py-1 text-center">Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {processLines.map((line) => {
              const active = activeProcess?.id === line.id;
              return (
                <tr
                  key={line.id}
                  {...getRowProps(line)}
                  className={`${onActiveProcessChange ? "cursor-pointer" : ""} ${active ? "bg-blue-50" : "bg-white"} ${isDragging(line) ? "opacity-50" : ""} ${isOver(line) ? "outline outline-2 outline-primary outline-offset-[-2px]" : ""}`}
                  onClick={() => {
                    if (consumeClickIfDragged()) return;
                    onActiveProcessChange?.(line.id);
                  }}
                >
                  {reorderEnabled && (
                    <td className="border px-1 py-1 text-center align-middle">
                      <SortableRowHandle handleProps={getHandleProps()} />
                    </td>
                  )}
                  <td className="border px-2 py-1 font-medium">{line.process?.description || "Proceso"}</td>
                  <td className="border px-2 py-1">
                    <MultipliedMoney value={costSheetProcessTotal(line).ownTotal} multiplier={multiplier} />
                  </td>
                  {showProcessActions && (
                    <td className="border px-1 py-1">
                      <div className="flex justify-end gap-1" onClick={(event) => event.stopPropagation()}>
                        {renderProcessActions(line)}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
            {processLines.length === 0 && (
              <tr>
                <td className="border px-2 py-3 text-center text-gray-500" colSpan={processColSpan}>{emptyProcessMessage}</td>
              </tr>
            )}
          </tbody>
        </table>
        {processFooter}
      </div>

      <div className="rounded border">
        <div className="flex min-h-[3.5rem] flex-col justify-center gap-1 border-b bg-gray-50 px-3 py-2 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="font-semibold">Materiales de {activeProcess?.process?.description || "proceso seleccionado"}</div>
            <div className="text-xs text-gray-500">
              {multiplier === 1 ? (
                <>Proceso {formatMoney(activeProcessTotals.ownTotal)} · Materiales {formatMoney(activeProcessTotals.materialTotal)} · Total {formatMoney(activeProcessTotals.total)}</>
              ) : (
                <>Coste/prenda {formatMoney(activeProcessTotals.total)} · Total {formatMoney(activeProcessTotals.total * multiplier)}</>
              )}
              {materialReorderEnabled ? " · Arrastra para cambiar el orden" : ""}
            </div>
          </div>
          {materialHeaderAction}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead className="bg-gray-100 text-left">
              <tr>
                {materialReorderEnabled && <th className="w-8 border px-1 py-1" aria-label="Reordenar" />}
                <th className="border px-2 py-1">Materia prima</th>
                <th className="w-20 border px-2 py-1 text-right">Cant.</th>
                <th className="w-20 border px-2 py-1 text-right">Precio</th>
                <th className="w-24 border px-2 py-1 text-right">Importe</th>
                {showMaterialActions && <th className="border px-2 py-1 text-center">Acciones</th>}
              </tr>
            </thead>
            <tbody>
              {materialLines.map((line) => {
                const lineCost = Number(line.line_cost ?? calculateMaterialLineCost(line));
                return (
                  <tr
                    key={line.id}
                    {...getMaterialRowProps(line)}
                    className={`bg-white ${isMaterialDragging(line) ? "opacity-50" : ""} ${isMaterialOver(line) ? "outline outline-2 outline-primary outline-offset-[-2px]" : ""}`}
                  >
                    {materialReorderEnabled && (
                      <td className="border px-1 py-1 text-center align-middle">
                        <SortableRowHandle handleProps={getMaterialHandleProps()} />
                      </td>
                    )}
                    <td className="border px-2 py-1">
                      <div className="font-bold">
                        {line.material_supplier?.supplier_ref || "Sin referencia"}
                        {line.material_supplier?.supplier?.name ? ` · ${line.material_supplier.supplier.name}` : ""}
                      </div>
                      <div className="text-xs text-gray-500">
                        {line.material?.internal_description || line.material?.code || "Material"}
                      </div>
                      {line.color_usage?.trim() && (
                        <div className="text-xs text-gray-500">Uso: {line.color_usage.trim()}</div>
                      )}
                      {String(line.notes || "").trim() && (
                        <div className="text-xs text-green-600">{line.notes.trim()}</div>
                      )}
                    </td>
                    <td className="border px-2 py-1"><MultipliedQuantity line={line} multiplier={multiplier} /></td>
                    <td className="border px-2 py-1 text-right">{formatMoney(line.unit_cost)}</td>
                    <td className="border px-2 py-1"><MultipliedMoney value={lineCost} multiplier={multiplier} /></td>
                    {showMaterialActions && (
                      <td className="border px-1 py-1">
                        <div className="flex justify-end gap-1">{renderMaterialActions(line)}</div>
                      </td>
                    )}
                  </tr>
                );
              })}
              {materialLines.length === 0 && (
                <tr>
                  <td className="border px-2 py-3 text-center text-gray-500" colSpan={materialColSpan}>{emptyMaterialMessage}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
